import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export type FeatureFlagKey =
  | "dispatch"
  | "gps_tracking"
  | "ai_reporting"
  | "fleet"
  | "scheduling"
  | "advanced_analytics"
  | "white_label"
  | "api_access"
  | "time_clock_enabled"
  | "sos_enabled"
  | "custom_reports";

export type FeatureFlags = Partial<Record<FeatureFlagKey, boolean>> & {
  loaded: boolean;
};

const DEFAULTS: Record<FeatureFlagKey, boolean> = {
  dispatch: true,
  gps_tracking: true,
  ai_reporting: true,
  fleet: true,
  scheduling: true,
  advanced_analytics: true,
  white_label: false,
  api_access: false,
  time_clock_enabled: true,
  sos_enabled: true,
  // Custom report templates ship on every plan (White Label and API Access
  // remain the only Enterprise-gated modules).
  custom_reports: true,

};

export function useFeatureFlags(): FeatureFlags {
  const { currentOrg } = useAuth();
  const [flags, setFlags] = useState<FeatureFlags>({ ...DEFAULTS, loaded: false });

  useEffect(() => {
    let cancelled = false;
    if (!currentOrg) {
      setFlags({ ...DEFAULTS, loaded: true });
      return;
    }
    (async () => {
      const { data } = await supabase
        .from("org_feature_flags")
        .select("*")
        .eq("organization_id", currentOrg.id)
        .maybeSingle();
      if (cancelled) return;
      if (!data) {
        // Self-heal: insert defaults row, then use defaults
        await supabase.from("org_feature_flags").insert({ organization_id: currentOrg.id });
        setFlags({ ...DEFAULTS, loaded: true });
        return;
      }
      setFlags({ ...DEFAULTS, ...(data as unknown as Record<string, boolean>), loaded: true });
    })();
    return () => { cancelled = true; };
  }, [currentOrg?.id]);

  return flags;
}

export function isFeatureEnabled(flags: FeatureFlags, key?: FeatureFlagKey): boolean {
  if (!key) return true;
  return flags[key] !== false;
}
